import useSWR, { mutate } from 'swr'
import { useCallback } from 'react'
import { signMessage } from '@wagmi/core'
import { wagmiConfig } from '@/constants/walletConnectionConfig'
import {
	getLinkedWallets,
	requestLinkChallenge,
	verifyLinkChallenge,
	unlinkWallet,
	type LinkedWallet
} from '@/utils/linkedWallets'

const LINKED_WALLETS_KEY = '/v1/wallets/linked'

const normalizeAddress = (addr?: string | null): `0x${string}` | undefined =>
	addr ? (addr.toLowerCase() as `0x${string}`) : undefined

export function useLinkedWallets() {
	const { data, error, isLoading } = useSWR<LinkedWallet[]>(
		LINKED_WALLETS_KEY,
		() => getLinkedWallets(),
		{
			revalidateOnFocus: false,
			dedupingInterval: 10_000,
			shouldRetryOnError: false
		}
	)

	const linkedWallets: LinkedWallet[] = data ?? []

	const isAddressLinked = useCallback(
		(address?: string | null) => {
			const target = normalizeAddress(address)
			if (!target) return false
			return linkedWallets.some(
				(w) => normalizeAddress(w.address) === target
			)
		},
		[linkedWallets]
	)

	const linkWallet = useCallback(
		async (address: `0x${string}`): Promise<LinkedWallet> => {
			const challenge = await requestLinkChallenge(address)

			let signature: string
			try {
				signature = await signMessage(wagmiConfig, {
					message: challenge.message,
					account: address
				})
			} catch (err: any) {
				const message = err?.shortMessage || err?.message || ''
				if (
					message.toLowerCase().includes('user rejected') ||
					message.toLowerCase().includes('user denied')
				) {
					throw new Error('Signature request rejected')
				}
				throw new Error(message || 'Failed to sign link challenge')
			}

			const linked = await verifyLinkChallenge(address, signature)
			await mutate(LINKED_WALLETS_KEY)
			return linked
		},
		[]
	)

	const removeLinkedWallet = useCallback(
		async (address: `0x${string}`): Promise<void> => {
			await unlinkWallet(address)
			await mutate(LINKED_WALLETS_KEY)
		},
		[]
	)

	return {
		linkedWallets,
		isLoading,
		error,
		linkWallet,
		removeLinkedWallet,
		isAddressLinked,
		refetch: () => mutate(LINKED_WALLETS_KEY)
	}
}
